import React, { useRef } from 'react'
import { Box, Backdrop } from '@mui/material'
import useImageZoom from '../hooks/useImageZoom'
import ImageControls from './ImageControls'
import ZoomableImage from './ZoomableImage'
import { backdropStyles, getThumbnailStyles } from './imageViewerStyles'

/**
 * ImageViewer - component for displaying and zooming images.
 *
 * @param {Object} props
 * @param {string} props.imageUrl - image URL to be displayed
 * @param {string} props.alt - alt text for the image
 * @param {'LEFT' | 'CENTER' | 'RIGHT'} [props.alignment='CENTER'] - alignment of the thumbnail image
 * @param {number} [props.zoomInitialMinScale=0.1] - Smallest possible scale factor for zoom.
 * @param {number} [props.zoomInitialMaxScale=4] - Largest possible scale factor for zoom.
 * @param {number} [props.zoomScaleStep=0.25] - Increment/decrement step for zoom controls.
 */
export default function ImageViewer({
  imageUrl,
  alt,
  alignment = 'CENTER',
  zoomInitialMinScale = 0.1,
  zoomInitialMaxScale = 4,
  zoomScaleStep = 0.25
}) {
  const imageRef = useRef(null)
  const backdropContentRef = useRef(null)

  const {
    open,
    scale,
    minScale: currentMinScale,
    maxScale: currentMaxScale,
    position,
    isDragging,
    showControls,
    currentFitScale,
    handleOpen,
    handleClose,
    resetView,
    handleZoomIn,
    handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useImageZoom({
    initialMinScale: zoomInitialMinScale,
    initialMaxScale: zoomInitialMaxScale,
    scaleStep: zoomScaleStep,
    containerRef: backdropContentRef,
    imageRef: imageRef
  })

  const onBackdropClick = event => {
    if (event.target === event.currentTarget && !isDragging) {
      handleClose()
    }
  }

  return (
    <>
      <Box
        component='img'
        src={imageUrl}
        alt={alt || 'Image'}
        sx={getThumbnailStyles(alignment)}
        onClick={handleOpen}
        data-testid='image-viewer-thumbnail'
      />

      <Backdrop
        open={open}
        onClick={onBackdropClick}
        sx={backdropStyles}
        TransitionProps={{
          timeout: 300
        }}
        data-testid='image-viewer-backdrop'
      >
        <Box
          ref={backdropContentRef}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <ZoomableImage
            imageUrl={imageUrl}
            alt={alt}
            scale={scale}
            position={position}
            isDragging={isDragging}
            imageRef={imageRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            isOpen={open}
          />
        </Box>

        <ImageControls
          showControls={showControls}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={resetView}
          onClose={handleClose}
          canZoomIn={scale < currentMaxScale - 0.001}
          canZoomOut={scale > currentMinScale + 0.001}
          currentScale={scale}
          fitScale={currentFitScale}
          currentPosition={position}
        />
      </Backdrop>
    </>
  )
}
